import { useEffect, useState } from "react";

import styled from "styled-components/macro";

import classNames from "classnames";
import { motion } from "framer-motion";

export const iPhoneMini = {
  width: 370,
  cssWidth: "370px",
  logoBottom: "calc(128px + ((100% - 128px) / 2))",
  inputPadding: "4px 6px 2px 9px",
};
export const iPhone = {
  width: 412,
  cssWidth: "412px",
  logoBottom: "calc(146px + ((100% - 146px) / 2))",
  inputPadding: "6px 8px 4px 11px",
};

export function useErrorShakeAnimation() {
  const [animateErrorShake, setAnimateErrorShake] = useState(false);

  const onAnimationComplete = () => setAnimateErrorShake(false);

  const shake = () =>
    setAnimateErrorShake((currentVal) =>
      currentVal === false ? 1 : currentVal + 1
    );

  const initial = { x: 0 };

  const shakeAnimation = {
    x: [6, -6, 6, 0],
    transition: { duration: 0.25 },
  };

  const animate = animateErrorShake ? shakeAnimation : initial;

  return {
    shake,
    animateErrorShake,
    onAnimationComplete,
    initial,
    animate,
    shakeAnimation,
    animationProps: {
      initial,
      animate,
      onAnimationComplete,
    },
  };
}

export function InputWrapper({ setIsHovering, errorShake, children }) {
  const { animationProps } = errorShake || {};

  const hoverProps = setIsHovering
    ? {
        onHoverStart: () => setIsHovering(true),
        onHoverEnd: () => setIsHovering(false),
      }
    : {};

  return (
    <InputWrapperStyle {...hoverProps} {...animationProps}>
      {children}
    </InputWrapperStyle>
  );
}

const InputWrapperStyle = styled(motion.div)`
  position: relative;
  margin-bottom: 4px;
  padding: 12px;
  padding-bottom: 8px;
  cursor: text;
  @media screen and (max-width: ${iPhone.cssWidth}) {
    padding: ${iPhone.inputPadding};
  }
  @media screen and (max-width: ${iPhoneMini.cssWidth}) {
    padding: ${iPhoneMini.inputPadding};
  }
`;

export const NumberInput = styled.input`
  appearance: none;
  outline: none;
  border: none;
  padding: 0;
  font-family: Montserrat;
  display: block;
  z-index: 500;
  background: none;
  color: #fff;
  width: 100%;
  font-size: 26px;
  height: 32px;
  font-variant-numeric: tabular-nums;
  text-shadow: rgba(0, 0, 0, 0.5) 1px 1px 2px;

  &::-webkit-input-placeholder,
  &::placeholder {
    color: ${({ useDarkPlaceholder }) =>
      useDarkPlaceholder
        ? "rgba(255, 255, 255, 0.6)"
        : "rgba(255, 255, 255, 0.4)"};
    font-size: 13px;
    text-shadow: ${({ useDarkPlaceholder }) =>
      useDarkPlaceholder ? "rgba(0, 0, 0, 0.8) 0 0 6px" : "none"};
    transform: translateY(-2px);
  }
  @media screen and (max-width: ${iPhone.cssWidth}) {
    font-size: 22px;
    height: 26px;
  }
  @media screen and (max-width: ${iPhoneMini.cssWidth}) {
    font-size: 20px;
    height: 24px;
  }

  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    -webkit-text-fill-color: white !important;
    transition: background-color 5000s ease-in-out 0s;
  }
`;

export const SmallNumberInput = styled(NumberInput)`
  font-size: 18px;
  height: 24px;
  margin-top: 1px;
  @media screen and (max-width: ${iPhone.cssWidth}) {
    font-size: 14px;
    height: 18px;
  }
`;

export function InputOutline({
  isFocused,
  isHovering,
  isEmpty,
  placeholder,
  style = {},
  placeholderAlign = "left",
}) {
  return (
    <OutlineWrapper style={style}>
      <Outline
        className={classNames({ isFocused, isHovering, isEmpty })}
        animate={
          isFocused
            ? { opacity: 1, scale: 1 }
            : isHovering || isEmpty
            ? { opacity: 0.6, scale: 0.98 }
            : { opacity: 0, scale: 0.95, transition: { type: "tween" } }
        }
      ></Outline>
      {placeholder && (
        <Placeholder
          style={
            placeholderAlign === "right"
              ? { left: "auto", right: "12px" }
              : null
          }
        >
          {placeholder}
        </Placeholder>
      )}
    </OutlineWrapper>
  );
}

const OutlineWrapper = styled.div`
  position: absolute;
  top: -12px;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: -5;
  @media screen and (max-width: ${iPhone.cssWidth}) {
    top: -15px;
  }
  @media screen and (max-width: ${iPhoneMini.cssWidth}) {
    top: -13px;
  }
`;

const Outline = styled.div`
  position: absolute;
  border-radius: 12px;
  border: 2px solid rgba(255, 255, 255, 0.6);
  background: rgba(255, 255, 255, 0.1);
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  opacity: 0;
  transform: scale(0.95);
  z-index: -4;
  transition: opacity 0.2s, transform 0.2s;
  &.isHovering,
  &.isEmpty {
    opacity: 0.6;
    transform: scale(0.98);
  }
  &.isFocused {
    opacity: 1;
    transform: none;
  }
`;

export const Placeholder = styled.div`
  font-family: Montserrat;
  font-size: 12px;
  height: 18px;
  line-height: 18px;
  position: absolute;
  text-transform: uppercase;
  top: 8px;
  left: 12px;
  user-select: none;
  z-index: -3;
  text-shadow: rgba(0, 0, 0, 0.5) 1px 1px 2px;
  @media screen and (max-width: ${iPhone.cssWidth}) {
    top: 4px;
    left: 10px;
    font-size: 10px;
  }
  @media screen and (max-width: ${iPhoneMini.cssWidth}) {
    top: 3px;
  }
`;

const ShineEl = styled(motion.div)`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-image: linear-gradient(
    -70deg,
    rgb(255, 255, 255, 0),
    rgb(255, 255, 255, 0),
    rgb(255, 255, 255, 0.4),
    rgb(255, 255, 255, 0),
    rgb(255, 255, 255, 0)
  );
`;

export function Shine() {
  return (
    <ShineEl
      initial={{ x: "-100%", opacity: 1 }}
      animate={{
        x: "100%",
        opacity: 0,
        transition: { duration: 0.6, delay: 0.1 },
      }}
    />
  );
}
